/**
 * Tap Tug — two-team tug-of-war remix of the tap-faster-to-win clicker.
 *
 * Rules
 *   - Players are auto-split into two teams (Red / Blue) by lobby order:
 *     even index → Red, odd index → Blue.
 *   - Every tap (touch / mouse / Space / Enter) pulls the rope toward the
 *     local player's team.
 *   - Combos (taps within COMBO_WINDOW_MS) build a per-player chain that
 *     boosts the pull strength via the SDK ComboSystem tier table.
 *   - The smaller team gets a balance multiplier of `larger / smaller` so
 *     1v2 / 2v3 stays winnable.
 *   - First team to drag the centre flag past their finish line
 *     (|ropePos| ≥ WIN_THRESHOLD) wins the match.
 *
 * Architecture
 *   - synchronized mode — host owns the rope position and the per-player
 *     combo state, broadcasts a snapshot at ~15 Hz.
 *   - taps are routed as one-shot SDK events ('tt-tap'). The host queues
 *     them per tick and resolves them in `tick()` so combo bookkeeping
 *     stays authoritative.
 *   - rendering is peer-local: rope wobble, dust kicks, encouragement
 *     banner and combo bursts spawn in response to snapshot deltas so
 *     visual feedback feels instant on every device.
 */
import {
  SdkGameScene,
  ComboSystem,
  CountdownDisplay,
  EncouragementBanner,
  HudPill,
  ParticleEmitter,
  CameraShakeHelper,
  TapRipple
} from '@platform/sdk';

// ─────────────────────────────────────────────────────────── tuning

// Rope position: -1.0 (Red wins) ↔ +1.0 (Blue wins). Crossing
// ±WIN_THRESHOLD ends the match.
const WIN_THRESHOLD = 0.95;
const TAP_BASE_PULL = 0.018;

// Combo decay window (ms). Tiers themselves come from the SDK's
// `ComboSystem` (DEFAULT_COMBO_TIERS) so the table stays in sync with
// horse-dash / puff-up.
const COMBO_WINDOW_MS = 360;

// Phase timings (ms)
const COUNTDOWN_MS = 3200;
const END_MS       = 5400;

// Teams
const TEAM_LEFT  = 'L';   // Red, anchor on the left
const TEAM_RIGHT = 'R';   // Blue, anchor on the right
const TEAM_COLORS = {
  L: 0xef4444,
  R: 0x3b82f6
};
const TEAM_NAMES = {
  L: 'RED',
  R: 'BLUE'
};

// HUD layout
const HUD_PILL_HEIGHT = 32;
const HUD_PILL_GAP    = 6;
const HUD_TOP         = 12;
const HUD_SIDE        = 12;

// Phase enum
const P_COUNTDOWN = 'countdown';
const P_TUG       = 'tug';
const P_END       = 'end';

// ─────────────────────────────────────────────────────────── scene

export default class GameScene extends SdkGameScene {

  start({ players }) {
    this.W = this.scale.width;
    this.H = this.scale.height;

    // Combo tier lookup. ComboSystem has no Phaser state, so it survives
    // resize without needing to be reconstructed in onResize.
    this._combo = new ComboSystem();

    // Build player lookup tables and team assignment from the roster.
    // Even-indexed players join the LEFT team, odd-indexed join RIGHT.
    this._players = (players || this.sdk.players || []).slice();
    this._playerNames = {};
    this._teamOf      = {};
    for (let i = 0; i < this._players.length; i++) {
      const p = this._players[i];
      this._playerNames[p.id] = p.name || p.id;
      this._teamOf[p.id] = (i % 2 === 0) ? TEAM_LEFT : TEAM_RIGHT;
    }

    // Compute team sizes + balance multiplier for the smaller team. The
    // multiplier equals largerSize / smallerSize so a 2-player team
    // facing a 3-player team has each tap counting for 1.5×.
    let leftSize = 0, rightSize = 0;
    for (const id of Object.keys(this._teamOf)) {
      if (this._teamOf[id] === TEAM_LEFT) leftSize++;
      else rightSize++;
    }
    this._teamSize = { L: leftSize, R: rightSize };
    const smaller = Math.min(leftSize, rightSize);
    const larger  = Math.max(leftSize, rightSize);
    const balanceMult = smaller > 0 ? larger / smaller : 1;
    this._teamBalance = {
      L: leftSize  <= rightSize ? balanceMult : 1,
      R: rightSize <= leftSize  ? balanceMult : 1
    };

    // Authoritative state. Host writes; clients overwrite via applySnapshot.
    this.state = {
      phase: P_COUNTDOWN,
      phaseAt: 0,
      phaseLen: COUNTDOWN_MS,
      ropePos: 0,                    // -1 (Red wins) ↔ +1 (Blue wins)
      players: this._initPlayers(),  // per-player combo state
      winnerTeam: null,              // 'L' / 'R'
      matchOver: false
    };

    // Host-only: queue of taps received this tick.
    this._tapQueue = [];

    if (this.sdk.isHost) {
      this._unsubTap = this.sdk.on('tt-tap', (payload) => {
        if (payload && payload.playerId) {
          this._tapQueue.push({ playerId: payload.playerId, ts: payload.ts || Date.now() });
        }
      });
    }

    // Last seen combo tier per player — used by every peer to fire the
    // tier-up cheer animation in response to snapshot deltas.
    this._lastComboTier = {};
    for (const id of Object.keys(this._playerNames)) this._lastComboTier[id] = 0;
    // Smoothed render rope position so the marker glides instead of
    // teleporting on every snapshot.
    this._renderRopePos = 0;

    this._lastResizeW = this.W;
    this._lastResizeH = this.H;

    // Scene construction
    this._buildBackground();
    this._buildField();
    this._buildPlayers();
    this._buildRope();
    this._buildParticleLayers();
    this._buildHud();
    this._buildOverlayText();
    this._buildEncouragement();
    this._buildBalanceBadge();

    this._setupLocalInput();
    this._startMusic();

    this.events.once('shutdown', () => this._teardown());
  }

  _initPlayers() {
    const out = {};
    for (const p of this._players) {
      out[p.id] = {
        combo: 0,
        comboTier: 0,
        lastTapAt: 0,
        taps: 0
      };
    }
    return out;
  }

  // ─────────────────────────────────────────────────── host simulation

  tick(dt, _inputs) {
    if (!this.sdk.isHost) return;
    if (this.state.matchOver) return;

    this.state.phaseAt += dt * 1000;
    const nowMs = Date.now();

    // Combo decay: chains break for any player who hasn't tapped within
    // the combo window. Run every tick so HUDs reflect the decay even
    // when a player stops tapping.
    if (this.state.phase === P_TUG) {
      for (const id of Object.keys(this.state.players)) {
        const p = this.state.players[id];
        if (p.combo > 0 && (nowMs - p.lastTapAt) > COMBO_WINDOW_MS) {
          p.combo = 0;
          p.comboTier = 0;
        }
      }
    }

    // Drain queued taps. Outside the TUG phase taps are discarded —
    // pressing during the countdown shouldn't give anyone an early lead.
    if (this.state.phase === P_TUG) {
      while (this._tapQueue.length) {
        const tap = this._tapQueue.shift();
        this._applyTap(tap.playerId, tap.ts);
      }
    } else {
      this._tapQueue.length = 0;
    }

    switch (this.state.phase) {
      case P_COUNTDOWN:
        if (this.state.phaseAt >= this.state.phaseLen) this._enterPhase(P_TUG, 0);
        break;

      case P_TUG:
        if (this.state.winnerTeam) this._enterPhase(P_END, END_MS);
        break;

      case P_END:
        if (this.state.phaseAt >= this.state.phaseLen) this._finalizeMatch();
        break;
    }
  }

  _enterPhase(phase, lenMs) {
    const prev = this.state.phase;
    this.state.phase = phase;
    this.state.phaseAt = 0;
    this.state.phaseLen = lenMs;
    this._onPhaseChanged(prev, phase);
  }

  _applyTap(playerId, ts) {
    const team = this._teamOf[playerId];
    if (!team) return;

    const p = this.state.players[playerId];
    if (!p) return;

    // Combo bookkeeping. If the gap since the last tap is short enough we
    // bump the combo counter; otherwise reset and start a new chain.
    if (ts - p.lastTapAt <= COMBO_WINDOW_MS) {
      p.combo += 1;
    } else {
      p.combo = 1;
    }
    p.lastTapAt = ts;
    p.taps += 1;

    // Resolve the active combo tier (highest tier whose `min` is met).
    const tier = this._combo.getTier(p.combo);
    p.comboTier = tier.index;
    const mult = tier.mult;

    // Apply pull. Sign comes from the team; magnitude from base × combo
    // tier × team balance bonus.
    const teamMult = this._teamBalance[team] || 1;
    const sign = team === TEAM_LEFT ? -1 : +1;
    this.state.ropePos += TAP_BASE_PULL * mult * teamMult * sign;
    if (this.state.ropePos > 1) this.state.ropePos = 1;
    else if (this.state.ropePos < -1) this.state.ropePos = -1;

    // Win check.
    if (this.state.ropePos >= WIN_THRESHOLD) {
      this.state.winnerTeam = TEAM_RIGHT;
    } else if (this.state.ropePos <= -WIN_THRESHOLD) {
      this.state.winnerTeam = TEAM_LEFT;
    }
  }

  _finalizeMatch() {
    if (this.state.matchOver) return;
    this.state.matchOver = true;

    // Per-player tap counts double as the GameOver score column.
    const scores = {};
    for (const id of Object.keys(this.state.players)) {
      scores[id] = this.state.players[id].taps;
    }

    // Winner name shown on the GameOver screen — combine the winning
    // team label with the names of its members so everyone gets credit.
    let winnerName = 'tie';
    if (this.state.winnerTeam) {
      const teamLabel = TEAM_NAMES[this.state.winnerTeam];
      const members = [];
      for (const id of Object.keys(this._teamOf)) {
        if (this._teamOf[id] === this.state.winnerTeam) {
          members.push(this._playerNames[id] || id);
        }
      }
      winnerName = teamLabel + ' team (' + members.join(', ') + ')';
    }

    this.sdk.endMatch({ winnerName, scores });
  }

  // ─────────────────────────────────────────────────── snapshots

  snapshot() {
    // Compact per-player payload — keep this lean since it ships at
    // ~15 Hz over the WebRTC channel.
    const players = {};
    for (const id of Object.keys(this.state.players)) {
      const p = this.state.players[id];
      players[id] = {
        c: p.combo,
        t: p.comboTier,
        l: p.lastTapAt,
        x: p.taps
      };
    }
    return {
      ph: this.state.phase,
      pa: this.state.phaseAt,
      pl: this.state.phaseLen,
      r:  round3(this.state.ropePos),
      p:  players,
      w:  this.state.winnerTeam,
      m:  this.state.matchOver ? 1 : 0
    };
  }

  applySnapshot(snap) {
    if (!snap) return;
    const prevPhase = this.state.phase;
    this.state.phase = snap.ph;
    this.state.phaseAt = snap.pa;
    this.state.phaseLen = snap.pl;
    this.state.ropePos = snap.r;
    this.state.winnerTeam = snap.w || null;
    this.state.matchOver = !!snap.m;

    if (snap.p) {
      for (const id of Object.keys(snap.p)) {
        let p = this.state.players[id];
        if (!p) {
          p = { combo: 0, comboTier: 0, lastTapAt: 0, taps: 0 };
          this.state.players[id] = p;
        }
        p.combo = snap.p[id].c;
        p.comboTier = snap.p[id].t;
        p.lastTapAt = snap.p[id].l;
        p.taps = snap.p[id].x;
      }
    }

    if (prevPhase !== this.state.phase) this._onPhaseChanged(prevPhase, this.state.phase);
  }

  _onPhaseChanged(prev, next) {
    if (next === P_TUG) {
      this._playSfx('tt_go');
      CameraShakeHelper.shake(this, 'hit');
    } else if (next === P_END) {
      this._playSfx('tt_win');
      this._spawnFireworks(this.state.winnerTeam);
    }
  }

  _playSfx(key) {
    if (this.sdk.audio && this.sdk.audio.playSfx) {
      try { this.sdk.audio.playSfx(key); } catch (_) { /* ignore */ }
    }
  }

  // ─────────────────────────────────────────── scene construction

  _buildBackground() {
    const W = this.W, H = this.H;
    const g = this.add.graphics().setDepth(0);

    // Sky gradient (blue → warm horizon).
    const stops = [
      { y: 0.00, color: 0x0c4a6e },
      { y: 0.25, color: 0x0369a1 },
      { y: 0.55, color: 0x60a5fa },
      { y: 0.62, color: 0xfde68a }
    ];
    for (let i = 0; i < stops.length - 1; i++) {
      g.fillStyle(stops[i].color, 1);
      g.fillRect(0, stops[i].y * H, W, (stops[i + 1].y - stops[i].y) * H + 2);
    }

    // Sun
    g.fillStyle(0xffe4a8, 0.55);
    g.fillCircle(W * 0.5, H * 0.55, 80);
    g.fillStyle(0xfff7e0, 0.8);
    g.fillCircle(W * 0.5, H * 0.55, 50);
    g.fillStyle(0xffffff, 0.95);
    g.fillCircle(W * 0.5, H * 0.55, 26);

    // Distant hills
    g.fillStyle(0x166534, 0.85);
    this._fillPoly(g, [
      [0, H * 0.62],
      [W * 0.18, H * 0.58],
      [W * 0.32, H * 0.61],
      [W * 0.50, H * 0.56],
      [W * 0.70, H * 0.60],
      [W * 0.88, H * 0.57],
      [W, H * 0.62],
      [W, H * 0.66],
      [0, H * 0.66]
    ]);

    // Cloud bokeh
    for (let i = 0; i < 6; i++) {
      const x = (i * 161 + 60) % W;
      const y = ((i * 47) % (H * 0.30)) + 24;
      g.fillStyle(0xffffff, 0.6);
      g.fillCircle(x,      y,     14);
      g.fillCircle(x + 14, y - 4, 11);
      g.fillCircle(x + 24, y + 2, 10);
      g.fillCircle(x - 12, y + 2, 9);
    }
  }

  _fillPoly(g, pts) {
    g.beginPath();
    g.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1]);
    g.closePath();
    g.fillPath();
  }

  _buildField() {
    const W = this.W, H = this.H;

    // The "field" lives below the horizon — a green strip with a muddy
    // patch in the middle and team-coloured tint stripes left and right.
    const fieldTop = H * 0.66;
    const fieldBottom = H - 6;
    const ropeY = (fieldTop + fieldBottom) / 2 - 6;

    this._fieldTop = fieldTop;
    this._fieldBottom = fieldBottom;
    this._ropeY = ropeY;
    // Anchors for the win lines and the rope-marker travel range.
    this._leftAnchorX  = 60;
    this._rightAnchorX = W - 60;
    this._centreX      = W / 2;

    const g = this.add.graphics().setDepth(1);

    // Grass strip
    g.fillStyle(0x16a34a, 1);
    g.fillRect(0, fieldTop, W, fieldBottom - fieldTop);
    // Lighter top edge
    g.fillStyle(0x22c55e, 1);
    g.fillRect(0, fieldTop, W, 8);
    // Darker bottom edge
    g.fillStyle(0x14532d, 0.6);
    g.fillRect(0, fieldBottom - 8, W, 8);

    // Red side tint
    g.fillStyle(0xef4444, 0.10);
    g.fillRect(0, fieldTop, W * 0.5, fieldBottom - fieldTop);
    // Blue side tint
    g.fillStyle(0x3b82f6, 0.10);
    g.fillRect(W * 0.5, fieldTop, W * 0.5, fieldBottom - fieldTop);

    // Mud puddle in the centre
    g.fillStyle(0x451a03, 0.65);
    g.fillEllipse(W / 2, ropeY + 22, W * 0.18, 24);
    g.fillStyle(0x78350f, 0.4);
    g.fillEllipse(W / 2, ropeY + 22, W * 0.16, 18);

    // Win lines — vertical bars at the team anchors. The rope marker
    // crossing one of these triggers the win.
    g.fillStyle(0xfee2e2, 0.95);
    g.fillRect(this._leftAnchorX  - 2, fieldTop, 4, fieldBottom - fieldTop);
    g.fillStyle(0xdbeafe, 0.95);
    g.fillRect(this._rightAnchorX - 2, fieldTop, 4, fieldBottom - fieldTop);

    // Centre dashed line
    g.fillStyle(0xffffff, 0.7);
    for (let y = fieldTop + 6; y < fieldBottom; y += 14) {
      g.fillRect(W / 2 - 1, y, 2, 7);
    }
  }

  _buildPlayers() {
    // Stick figures for every player on each team. The local player gets
    // a gold ring outline so they can pick themselves out at a glance.
    this.playerSprites = {};
    const W = this.W;
    const fieldH = this._fieldBottom - this._fieldTop;

    const localId = this.sdk.localPlayerId;

    for (const team of [TEAM_LEFT, TEAM_RIGHT]) {
      const ids = Object.keys(this._teamOf).filter((id) => this._teamOf[id] === team);
      const n = ids.length;
      const teamColor = TEAM_COLORS[team];

      for (let i = 0; i < n; i++) {
        const id = ids[i];
        // Spread players vertically inside the team's side of the field.
        const slotY = this._fieldTop + fieldH * (n === 1 ? 0.5 : (0.32 + 0.36 * (i / (n - 1))));
        // Players sit a small horizontal offset from the anchor; the
        // first player is closest to the anchor (back of the team), the
        // last is closest to the rope.
        const dxFromAnchor = 18 + i * 22;
        const baseX = team === TEAM_LEFT
          ? this._leftAnchorX  + dxFromAnchor
          : this._rightAnchorX - dxFromAnchor;

        const c = this.add.container(baseX, slotY).setDepth(20);

        const g = this.add.graphics();
        // Body (rounded rect) with team tint
        g.fillStyle(teamColor, 1);
        g.fillRoundedRect(-7, -8, 14, 18, 4);
        // Belt
        g.fillStyle(0x111827, 0.9);
        g.fillRect(-7, 4, 14, 3);
        // Head
        g.fillStyle(0xfde68a, 1);
        g.fillCircle(0, -14, 6);
        g.lineStyle(1, 0x111827, 1);
        g.strokeCircle(0, -14, 6);
        // Eyes
        g.fillStyle(0x111827, 1);
        g.fillCircle(team === TEAM_LEFT ? 2 : -2, -15, 1);
        // Legs
        g.fillStyle(0x111827, 1);
        g.fillRect(-5, 10, 3, 10);
        g.fillRect(2,  10, 3, 10);
        // Arms — drawn separately so we can rotate the "tugging" arm.

        c.add(g);

        // Tugging arm: a short rounded line. We anchor it at the
        // shoulder and rotate it on every tap.
        const armColor = team === TEAM_LEFT ? 0xfca5a5 : 0x93c5fd;
        const arm = this.add.graphics();
        arm.fillStyle(armColor, 1);
        arm.fillRoundedRect(0, -2, 14, 4, 2);
        arm.x = team === TEAM_LEFT ? 6 : -6;
        arm.y = -4;
        arm.rotation = team === TEAM_LEFT ? 0 : Math.PI;
        c.add(arm);

        // Local-player highlight ring
        if (id === localId) {
          const ring = this.add.graphics();
          ring.lineStyle(3, 0xfde047, 0.95);
          ring.strokeCircle(0, -2, 22);
          c.add(ring);
        }

        // Name tag above the head
        const label = this.add.text(0, -28, (this._playerNames[id] || '').slice(0, 8), {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '10px',
          fontStyle: 'bold',
          color: '#ffffff',
          stroke: '#000000',
          strokeThickness: 3
        }).setOrigin(0.5);
        c.add(label);

        this.playerSprites[id] = {
          container: c,
          arm,
          baseX,
          baseY: slotY,
          team,
          armBase: arm.rotation,
          armTug: 0
        };
      }
    }
  }

  _buildRope() {
    // Rope is rendered each frame based on this.state.ropePos so we
    // store the graphics object once and clear/redraw in update().
    this.ropeGraphics = this.add.graphics().setDepth(18);

    // Marker container — a red flag tied to the rope, with a small label
    // showing the live rope position percentage.
    this.markerContainer = this.add.container(this._centreX, this._ropeY).setDepth(19);
    this.markerGraphics = this.add.graphics();
    this.markerContainer.add(this.markerGraphics);
  }

  _buildParticleLayers() {
    // Reusable particle pool from the SDK. Replaces the hand-rolled
    // `_particles` array + `_updateParticles` loop the game used to keep.
    this._fx = new ParticleEmitter({ scene: this, depth: 15 });
  }

  _buildHud() {
    const W = this.W;
    this.hudContainer = this.add.container(0, 0).setDepth(100);
    this.hudRows = {};

    const ids = Object.keys(this._playerNames);
    const n = ids.length;

    const twoCols = n > 4;
    const innerMargin = HUD_SIDE;
    const leftCount = twoCols ? Math.ceil(n / 2) : n;
    const pillWidth = twoCols
      ? (W - innerMargin * 3) / 2
      : W - innerMargin * 2;

    for (let i = 0; i < n; i++) {
      const id = ids[i];
      const isLeft = i < leftCount;
      const colIndex = isLeft ? i : (i - leftCount);
      const xOrigin = isLeft ? innerMargin : (innerMargin * 2 + pillWidth);
      const y = HUD_TOP + colIndex * (HUD_PILL_HEIGHT + HUD_PILL_GAP);
      const pill = this._buildHudRow(id, xOrigin, y, pillWidth);
      this.hudContainer.add(pill.container);
      this.hudRows[id] = pill;
    }
  }

  _buildHudRow(playerId, originX, originY, width) {
    const team = this._teamOf[playerId];
    const color = TEAM_COLORS[team] || 0xffffff;
    const name = this._playerNames[playerId] || playerId;

    // SDK HudPill gives us the bg + color tag + name + right-aligned
    // metric for free. tap-tug has no middle slot (no progress bar — the
    // shared rope marker IS the progress indicator), so we omit the
    // middleSlot callback.
    const pill = new HudPill({
      scene: this,
      x: originX, y: originY,
      width,
      height: HUD_PILL_HEIGHT,
      color,
      name,
      metric: 'x0',
      maxNameChars: 9
    });

    // Add the team initial letter inside the colored tag — this is
    // tap-tug-specific (other games don't put text inside the tag).
    const tagLabel = this.add.text(17, 16, TEAM_NAMES[team][0], {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#ffffff'
    }).setOrigin(0.5);
    pill.container.add(tagLabel);

    pill._shownCombo = 0;
    return pill;
  }

  _renderHud() {
    if (!this.hudRows) return;
    for (const id of Object.keys(this.hudRows)) {
      const pill = this.hudRows[id];
      const p = this.state.players[id];
      if (!p) continue;

      if (p.combo !== pill._shownCombo) {
        pill._shownCombo = p.combo;
        const tier = this._combo.tiers[p.comboTier] || this._combo.tiers[0];
        pill.setMetric('x' + p.combo, tier.color);
        if (p.combo > 0) pill.pulseMetric();
      }
    }
  }

  _buildOverlayText() {
    const W = this.W, H = this.H;

    this.statusText = this.add.text(W / 2, H * 0.30, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '30px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#0f172a',
      strokeThickness: 6,
      align: 'center'
    }).setOrigin(0.5).setDepth(200);

    // Big "3 → 2 → 1 → GO!" — was a hand-rolled text + tween, now the
    // CountdownDisplay helper. We re-create on resize.
    this._countdown = new CountdownDisplay({ scene: this });

    this.instructionsText = this.add.text(W / 2, H - 14,
      'TAP THE SCREEN TO PULL THE ROPE!', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5, 1).setDepth(200);
  }

  _buildEncouragement() {
    // Centered "GOOD!/GREAT!/ON FIRE!" flash — handled by the SDK helper.
    this._encourage = new EncouragementBanner({ scene: this });
  }

  _buildBalanceBadge() {
    // If teams are uneven, show a small "RED ×1.5" badge at the top so
    // the smaller team knows about its handicap. We hide it for fair
    // matches to avoid HUD noise.
    const localTeam = this._teamOf[this.sdk.localPlayerId];
    const localBoost = this._teamBalance[localTeam] || 1;
    if (Math.abs(localBoost - 1) < 0.01) {
      this.balanceBadge = null;
      return;
    }
    const txt = TEAM_NAMES[localTeam] + ' BOOST x' + (Math.round(localBoost * 100) / 100);
    this.balanceBadge = this.add.text(this.W / 2, HUD_TOP + (HUD_PILL_HEIGHT + HUD_PILL_GAP) * Object.keys(this._playerNames).length + 4, txt, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#ffffff',
      backgroundColor: '#0f172a',
      padding: { x: 8, y: 3 }
    }).setOrigin(0.5, 0).setDepth(101);
  }

  /** Rebuild HUD + field layout when the viewport changes. */
  onResize(width, height) {
    if (!width || !height) return;
    if (width === this._lastResizeW && height === this._lastResizeH) return;
    this._lastResizeW = width;
    this._lastResizeH = height;
    this.W = width;
    this.H = height;

    // Wipe the entire display list and rebuild from new dimensions.
    // The SDK helpers (CountdownDisplay, EncouragementBanner, ParticleEmitter,
    // HudPill) own Phaser objects that get destroyed by `removeAll(true)`,
    // so we re-construct them in the build calls below.
    this.children.removeAll(true);

    this._buildBackground();
    this._buildField();
    this._buildPlayers();
    this._buildRope();
    this._buildParticleLayers();
    this._buildHud();
    this._buildOverlayText();
    this._buildEncouragement();
    this._buildBalanceBadge();
  }

  // ─────────────────────────────────────────── per-frame rendering

  update(time, delta) {
    // Clients smooth the phase timer between snapshots so animations
    // don't wait for the next host message to progress.
    if (!this.sdk.isHost && !this.state.matchOver) {
      this.state.phaseAt += delta;
    }
    const dt = delta / 1000;

    // Glide the rendered rope position toward the authoritative number
    // so the marker doesn't jitter on each tap.
    const lerp = Math.min(1, dt * 12);
    this._renderRopePos += (this.state.ropePos - this._renderRopePos) * lerp;

    this._renderHud();
    this._renderRope(dt);
    this._renderPlayers(dt);
    this._renderCountdown();
    this._renderStatusText();
    this._fx.update(dt);
    this._detectComboTransitions();
  }

  _renderRope(dt) {
    const g = this.ropeGraphics;
    g.clear();

    const xRange = (this._rightAnchorX - this._leftAnchorX) / 2;
    const markerX = this._centreX + this._renderRopePos * xRange;
    const ropeY = this._ropeY;

    // Subtle wobble proportional to active tug strength on each side.
    const wobble = Math.sin(performance.now() * 0.012) * 1.2;

    // Rope body — thick brown line with a slight curve through the
    // marker. We draw it as a polyline so the curve looks like a real
    // rope sagging through the centre point.
    const segs = 22;
    g.lineStyle(7, 0x451a03, 1);
    g.beginPath();
    for (let i = 0; i <= segs; i++) {
      const tx = i / segs;
      // Interpolate from leftAnchor through markerX to rightAnchor.
      let x;
      if (tx < 0.5) {
        const u = tx * 2;
        x = this._leftAnchorX + (markerX - this._leftAnchorX) * u;
      } else {
        const u = (tx - 0.5) * 2;
        x = markerX + (this._rightAnchorX - markerX) * u;
      }
      // Sag the rope downward at the centre, modulated by the wobble.
      const sag = Math.sin(tx * Math.PI) * (8 + wobble);
      const y = ropeY + sag;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.strokePath();

    // Highlight strand
    g.lineStyle(2, 0xa16207, 0.85);
    g.beginPath();
    for (let i = 0; i <= segs; i++) {
      const tx = i / segs;
      let x;
      if (tx < 0.5) {
        const u = tx * 2;
        x = this._leftAnchorX + (markerX - this._leftAnchorX) * u;
      } else {
        const u = (tx - 0.5) * 2;
        x = markerX + (this._rightAnchorX - markerX) * u;
      }
      const sag = Math.sin(tx * Math.PI) * (8 + wobble);
      const y = ropeY + sag - 1.5;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.strokePath();

    // Marker — red flag at the rope position.
    this.markerContainer.x = markerX;
    this.markerContainer.y = ropeY + Math.sin(0.5 * Math.PI) * 8; // sit at the bottom of the sag
    const m = this.markerGraphics;
    m.clear();
    // Pole
    m.fillStyle(0x451a03, 1);
    m.fillRect(-1.5, -28, 3, 30);
    // Flag — colour shifts toward the side currently winning
    const winningColor = this._renderRopePos < 0 ? TEAM_COLORS.L : TEAM_COLORS.R;
    m.fillStyle(winningColor, 1);
    m.beginPath();
    m.moveTo(1.5, -26);
    m.lineTo(18, -22);
    m.lineTo(1.5, -16);
    m.closePath();
    m.fillPath();
    // White outline
    m.lineStyle(1.5, 0xffffff, 0.95);
    m.beginPath();
    m.moveTo(1.5, -26);
    m.lineTo(18, -22);
    m.lineTo(1.5, -16);
    m.closePath();
    m.strokePath();
    // Knot at the rope
    m.fillStyle(0x000000, 0.7);
    m.fillCircle(0, 0, 4);
    m.fillStyle(winningColor, 1);
    m.fillCircle(0, 0, 3);
  }

  _renderPlayers(dt) {
    const nowMs = Date.now();
    for (const id of Object.keys(this.playerSprites)) {
      const sprite = this.playerSprites[id];
      const p = this.state.players[id];
      if (!p) continue;

      // Tug intensity = how recently they tapped + their combo tier.
      const sinceTap = nowMs - p.lastTapAt;
      const recent = sinceTap < COMBO_WINDOW_MS;
      const tier = p.comboTier;

      // Lean back when actively tugging — a small horizontal offset
      // toward the team's anchor.
      const leanAmt = recent ? 4 + tier * 1.5 : 0;
      const leanDir = sprite.team === TEAM_LEFT ? -1 : +1;
      sprite.container.x = sprite.baseX + leanDir * leanAmt;

      // Bob the player's body slightly with the tug rhythm.
      const bob = recent ? Math.sin(nowMs * 0.025) * (2 + tier * 0.4) : 0;
      sprite.container.y = sprite.baseY + bob;

      // Ease the tugging arm angle back to the rest pose if no recent
      // tap. _onLocalTap pushes sprite.armTug to 1.0 each time.
      sprite.armTug = Math.max(0, sprite.armTug - dt * 4.5);
      const armSwing = sprite.armTug * 0.6 * (sprite.team === TEAM_LEFT ? 1 : -1);
      sprite.arm.rotation = sprite.armBase + armSwing;
    }
  }

  _detectComboTransitions() {
    // Whenever a player crosses up into a higher combo tier, fire a
    // bright cheer + screen flash on every peer.
    for (const id of Object.keys(this.state.players)) {
      const p = this.state.players[id];
      const last = this._lastComboTier[id] || 0;
      if (p.comboTier > last) this._onComboLevelUp(id, p.comboTier);
      this._lastComboTier[id] = p.comboTier;

      // Drive the player's "tug" animation in response to authoritative
      // tap counts. We mark the per-frame interaction by bumping armTug
      // when the snapshot's `taps` increases. We track that here:
      const sprite = this.playerSprites[id];
      if (sprite) {
        if (sprite._lastTaps == null) sprite._lastTaps = p.taps;
        if (p.taps > sprite._lastTaps) {
          sprite.armTug = 1;
          sprite._lastTaps = p.taps;
        }
      }
    }
  }

  _onComboLevelUp(playerId, tierIdx) {
    const tier = this._combo.tiers[tierIdx];
    if (!tier || tierIdx === 0) return;

    if (playerId === this.sdk.localPlayerId) {
      this._encourage.show(tier.label, tier.color);
      this._playSfx(tierIdx >= 3 ? 'tt_cheer' : 'tt_combo');
      CameraShakeHelper.combo(this, tierIdx);
    } else {
      this._playSfx('tt_combo');
    }

    // Burst around the level-up player so other peers can see who hit it.
    const sprite = this.playerSprites[playerId];
    if (sprite) {
      this._fx.burst(sprite.container.x, sprite.container.y, { color: tier.color });
      this._fx.sparkle(sprite.container.x, sprite.container.y);
    }
  }

  _renderCountdown() {
    if (this.state.phase !== P_COUNTDOWN) {
      this._countdown.hide();
      return;
    }
    const remainingMs = Math.max(0, this.state.phaseLen - this.state.phaseAt);
    // CountdownDisplay returns the new label only on the frame the
    // visible text changes, so we play the tick / go SFX in sync with
    // the visible tween.
    const ticked = this._countdown.update(remainingMs);
    if (ticked === 'GO!') this._playSfx('tt_go');
    else if (ticked) this._playSfx('tt_tick');
  }

  _renderStatusText() {
    let msg = '';
    if (this.state.phase === P_COUNTDOWN) {
      msg = 'GET READY TO TUG!';
    } else if (this.state.phase === P_TUG) {
      // Show which side is currently leading.
      if (Math.abs(this._renderRopePos) < 0.05) msg = 'PULL!';
      else if (this._renderRopePos < 0) msg = 'RED PULLING!';
      else msg = 'BLUE PULLING!';
    } else if (this.state.phase === P_END) {
      const wt = this.state.winnerTeam;
      msg = wt ? (TEAM_NAMES[wt] + ' TEAM WINS! 🏆') : 'TIE!';
    }

    if (this.statusText.text !== msg) {
      this.statusText.setText(msg);
      if (msg) {
        this.tweens.killTweensOf(this.statusText);
        this.statusText.setScale(1.4);
        this.tweens.add({
          targets: this.statusText,
          scale: 1,
          duration: 260,
          ease: 'Back.out'
        });
      }
    }
  }

  // ─────────────────────────────────────────────────── particles
  // The hand-rolled spawn / update methods used to live here. They're now
  // one-line wrappers around the SDK's `ParticleEmitter` (this._fx) and
  // `TapRipple` static helper. Only the per-game tuning (tug-dust palette,
  // fireworks colors) lives here; the physics + render are SDK code.

  _spawnLocalTugDust(playerId, tier) {
    const sprite = this.playerSprites[playerId];
    if (!sprite) return;
    const team = sprite.team;
    const x = sprite.container.x + (team === TEAM_LEFT ? +6 : -6);
    const y = sprite.container.y + 16;
    const palette = tier && tier.color !== 0xffffff
      ? [tier.color, tier.glow, 0xfde68a]
      : [0xfde68a, 0xfbbf24, 0xfef3c7];
    // Aim the puff away from the player's body, toward the rope.
    this._fx.cloud(x, y, {
      count: 4,
      palette,
      angleBase: team === TEAM_LEFT ? 0 : Math.PI,
      angleSpread: 0.7,
      speed: [40, 90],
      ttl: 0.55
    });
  }

  _spawnFireworks(winningTeam) {
    const baseColor = winningTeam ? TEAM_COLORS[winningTeam] : 0xfacc15;
    this._fx.fireworks({
      count: 5,
      burstSize: 38,
      palette: [baseColor, 0xfacc15, 0xffffff, baseColor],
      onPop: () => this._playSfx('tt_tick')
    });
  }

  // ─────────────────────────────────────────────────── local input

  _setupLocalInput() {
    const onTap = (pointer) => {
      if (this.state.matchOver) return;
      if (this.state.phase !== P_TUG) {
        // Tap during countdown — give a tiny ack but don't count.
        if (pointer) TapRipple.spawn(this, { x: pointer.x, y: pointer.y, alpha: 0.4 });
        return;
      }
      this._fireLocalTap();
      if (pointer) {
        const team = this._teamOf[this.sdk.localPlayerId];
        TapRipple.spawn(this, {
          x: pointer.x, y: pointer.y,
          color: TEAM_COLORS[team] || 0xffffff,
          alpha: 0.95
        });
      }
    };

    this.input.on('pointerdown', onTap);

    if (this.input.keyboard) {
      this.input.keyboard.on('keydown-SPACE', () => onTap(null));
      this.input.keyboard.on('keydown-ENTER', () => onTap(null));
    }
  }

  _fireLocalTap() {
    const playerId = this.sdk.localPlayerId;
    const payload = { playerId, ts: Date.now() };
    if (this.sdk.isHost) {
      this._tapQueue.push(payload);
    } else {
      this.sdk.emit('tt-tap', payload);
    }
    // Optimistic local feedback — play the grunt + dust immediately so
    // it stays in sync with the player's finger rather than waiting for
    // the snapshot round-trip.
    this._playSfx('tt_tug');
    const localTier = this.state.players[playerId]
      ? this._combo.tiers[this.state.players[playerId].comboTier]
      : this._combo.tiers[0];
    this._spawnLocalTugDust(playerId, localTier);
    // Also flick the local player's arm immediately for snappy feedback.
    const sprite = this.playerSprites[playerId];
    if (sprite) sprite.armTug = 1;
  }

  // ─────────────────────────────────────────────────── music

  _startMusic() {
    if (this.sdk.audio && this.sdk.audio.playMusic) {
      try { this.sdk.audio.playMusic('tt_music'); } catch (_) { /* ignore */ }
    }
  }

  _teardown() {
    try { if (this.sdk.audio && this.sdk.audio.stopMusic) this.sdk.audio.stopMusic(); } catch (_) {}
    try { if (this._unsubTap) this._unsubTap(); } catch (_) {}
  }
}

// ───────────────────────────────────────────────────────── helpers

function round3(v) { return Math.round(v * 1000) / 1000; }
